/**
 * QueueStatsHeader Component
 * 
 * Displays session statistics and status indicators for the Hot Lead Queue.
 * Shows claimed today count, pending actions, and blocking warnings.
 */

import React from 'react';
import {
  Box,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Progress,
  Text,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  FiTarget, 
  FiCheckCircle, 
  FiClock, 
  FiAlertTriangle,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';

export function QueueStatsHeader({ stats, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const warningBg = useColorModeValue('orange.50', 'orange.900');

  const {
    availableCount = 0,
    claimedToday = 0,
    pendingUnlock = [],
    isBlocked = false,
    blockedCount = 0,
    maxAllowed = 5,
    currentlyActive = 0,
  } = stats || {};

  const progressValue = (blockedCount / maxAllowed) * 100;

  return (
    <Box mb={6}>
      {/* Blocking Warning Banner */}
      {isBlocked && (
        <Alert status="warning" mb={4} borderRadius="md" bg={warningBg}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Claiming Blocked</AlertTitle>
            <AlertDescription>
              You have {blockedCount} unworked leads. Log activity on them to unlock new claims.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Stats Cards */}
      <Flex 
        gap={4} 
        flexWrap="wrap"
        direction={{ base: 'column', md: 'row' }}
      >
        {/* Available Leads */}
        <Box
          flex="1"
          minW={{ base: '100%', md: '200px' }}
          p={4}
          bg={cardBg}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <Stat>
            <Flex justify="space-between" align="flex-start">
              <Box>
                <StatLabel color="gray.500" fontSize="sm">
                  Available Leads
                </StatLabel>
                <StatNumber fontSize="2xl" color="blue.500">
                  {loading ? '...' : availableCount.toLocaleString()}
                </StatNumber>
                <StatHelpText mb={0}>
                  <Icon as={FiTarget} mr={1} />
                  Ready to claim
                </StatHelpText>
              </Box>
              <Icon as={FiUsers} boxSize={8} color="blue.400" opacity={0.7} />
            </Flex>
          </Stat>
        </Box>

        {/* Claimed Today */}
        <Box
          flex="1"
          minW={{ base: '100%', md: '200px' }}
          p={4}
          bg={cardBg}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <Stat>
            <Flex justify="space-between" align="flex-start">
              <Box>
                <StatLabel color="gray.500" fontSize="sm">
                  Claimed Today
                </StatLabel>
                <StatNumber fontSize="2xl" color="green.500">
                  {claimedToday}
                </StatNumber>
                <StatHelpText mb={0}>
                  <Icon as={FiCheckCircle} mr={1} />
                  Keep going!
                </StatHelpText>
              </Box>
              <Icon as={FiTrendingUp} boxSize={8} color="green.400" opacity={0.7} />
            </Flex>
          </Stat>
        </Box>

        {/* Active Leads */}
        <Box
          flex="1"
          minW={{ base: '100%', md: '200px' }}
          p={4}
          bg={cardBg}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <Stat>
            <Flex justify="space-between" align="flex-start">
              <Box>
                <StatLabel color="gray.500" fontSize="sm">
                  Active Leads
                </StatLabel>
                <StatNumber fontSize="2xl" color="purple.500">
                  {currentlyActive}
                </StatNumber>
                <StatHelpText mb={0}>
                  <Icon as={FiClock} mr={1} />
                  Being worked
                </StatHelpText>
              </Box>
              <Icon as={FiClock} boxSize={8} color="purple.400" opacity={0.7} />
            </Flex>
          </Stat>
        </Box>

        {/* Claim Capacity */}
        <Box
          flex="1.5"
          minW={{ base: '100%', md: '280px' }}
          p={4}
          bg={isBlocked ? 'orange.50' : cardBg}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={isBlocked ? 'orange.300' : borderColor}
          boxShadow="sm"
        >
          <Stat>
            <Flex justify="space-between" align="flex-start" mb={2}>
              <Box>
                <StatLabel color="gray.500" fontSize="sm">
                  Claim Capacity
                </StatLabel>
                <HStack spacing={2} mt={1}>
                  <StatNumber fontSize="xl">
                    {maxAllowed - blockedCount} / {maxAllowed}
                  </StatNumber>
                  <Badge 
                    colorScheme={isBlocked ? 'red' : blockedCount > 2 ? 'yellow' : 'green'}
                    fontSize="xs"
                  >
                    {isBlocked ? 'BLOCKED' : blockedCount > 2 ? 'LOW' : 'AVAILABLE'}
                  </Badge>
                </HStack>
              </Box>
              <Icon 
                as={isBlocked ? FiAlertTriangle : FiCheckCircle} 
                boxSize={8} 
                color={isBlocked ? 'orange.500' : 'green.400'} 
                opacity={0.7} 
              />
            </Flex>
            <Tooltip 
              label={`${blockedCount} of ${maxAllowed} unworked leads`}
              placement="bottom"
            >
              <Box>
                <Progress 
                  value={progressValue} 
                  size="sm" 
                  colorScheme={isBlocked ? 'red' : blockedCount > 2 ? 'yellow' : 'green'}
                  borderRadius="full"
                  bg="gray.200"
                />
              </Box>
            </Tooltip>
            {blockedCount > 0 && (
              <Text fontSize="xs" color="gray.500" mt={2}>
                {blockedCount} lead{blockedCount !== 1 ? 's' : ''} need activity to unlock claiming
              </Text>
            )}
          </Stat>
        </Box>
      </Flex>
    </Box>
  );
}

export default QueueStatsHeader;
